import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaymentMethod } from '@core/types';
import Layout from '../componentes/Layout';
import SaudeNegocio from '../componentes/SaudeNegocio';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import SemDado from '../componentes/SemDado';
import {
  custosMensais, folhaDoMes, pagarFolha, perfis, removerLancamentoFolha,
  resultadoMensal, salvarLancamentoFolha, termosDaEquipe, vendasPorVendedor,
} from '../lib/consultas';
import { baixarCsv, paraCsv } from '../lib/csv';
import { brl, dataBR, hojeISO, mesISO, paraNumero } from '../lib/formato';
import {
  comissaoDoCampo, dataDoPagamento, diaDePagarAFolha, faltaAComissao, totalFolha, validarDiaPago,
  validarFolha,
} from '../lib/folha';
import { useFolhaPrevista } from '../lib/useFolhaPrevista';
import { mensagemDeErro } from '../lib/supabase';

// Tela 12 — Painel do dono. Responde três perguntas que hoje ninguém responde:
// quanto entrou, para onde foi, e o que sobrou.
//
// A folha NÃO é uma planilha à parte: pagar uma linha aqui chama a RPC
// pagar_folha, que lança a saída no caixa na mesma transação. É o que impede a
// divergência clássica de "paguei mas não lancei".
//
// A comissão vem sugerida do que a pessoa vendeu no mês (v_vendas_por_vendedor
// × comissao_percent), mas é sempre editável — o número final é decisão do dono.
//
// DESDE A 094 (08/10/2026) a linha do mês corrente e a do seguinte já NASCEM
// do combinado (gerar_folha_prevista, ao abrir esta tela, Contas a pagar ou o
// Fluxo): "Lançar na folha" virou "Salvar alterações" para quem já tem salário.
// Salvar aqui grava prevista = false — o que o dono ajustou não é reescrito
// quando o combinado muda. E a mesma linha aparece em Contas a pagar: pagar
// lá ou aqui é a mesma coisa.

const FORMAS: PaymentMethod[] = ['pix', 'transferencia', 'dinheiro', 'boleto'];

/** A linha do mês em v_resultado_mensal — só os quatro números do topo. */
type NumerosDoMes = { receitas: unknown; custos: unknown; folha: unknown; resultado: unknown };

/** Em que pé está a consulta do resultado (`v_resultado_mensal`). */
export type EstadoDoResultado = 'carregando' | 'erro' | 'pronto';

/**
 * OS QUATRO NÚMEROS DO MÊS (Grafite, 29/10/2026).
 *
 *  - "[sem dado]" é SÓ para a consulta que VOLTOU sem a linha do mês: zero é
 *    fato medido, e um mês que o caixa não registrou não foi medido. Enquanto
 *    a consulta não volta, é "…"; se ela falhou (4G ruim), é "—" e uma linha
 *    dizendo que não carregou — "[sem dado]" ali seria afirmar que o mês não
 *    tem registro, quando o que faltou foi a rede (revisão do #359).
 *  - O número em mono, com os algarismos alinhados, e CLARO. Receita não é
 *    verde nem custo é vermelho: no Grafite a cor é sinal, e custo não é
 *    prejuízo. O único vermelho é o resultado NEGATIVO — o vermelho de
 *    prejuízo (`text-erro`), que não é o da marca (regra 10 do CLAUDE.md).
 *  - Duas colunas até o tablet, quatro no computador: o mono é mais largo, e a
 *    640 px quatro colunas cortariam o valor.
 */
export function NumerosDoMes({ doMes, estado }: {
  doMes: NumerosDoMes | undefined;
  estado: EstadoDoResultado;
}) {
  const cartao = (rotulo: string, campo: keyof NumerosDoMes) => {
    const n = Number(doMes?.[campo] ?? 0);
    return (
      <div className="cartao p-3 sm:p-4" data-numero-do-mes={campo}>
        <p className="rotulo">{rotulo}</p>
        {estado === 'carregando' ? (
          <p className="numero text-base text-dim sm:text-lg" aria-label="Carregando">…</p>
        ) : estado === 'erro' ? (
          <p className="numero text-base text-dim sm:text-lg" aria-label="Não carregou">—</p>
        ) : doMes ? (
          <p className={[
            'numero text-base font-medium whitespace-nowrap sm:text-lg xl:text-xl',
            campo === 'resultado' && n < 0 ? 'text-erro' : 'text-paper',
          ].join(' ')}>
            {brl(n)}
          </p>
        ) : (
          <p className="text-base sm:text-lg"><SemDado /></p>
        )}
      </div>
    );
  };
  return (
    <section className="space-y-2">
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        {cartao('Receitas', 'receitas')}
        {cartao('Custos', 'custos')}
        {cartao('Folha', 'folha')}
        {cartao('Resultado', 'resultado')}
      </div>
      {/* A linha discreta do erro: o motivo já está no Aviso do topo da tela. */}
      {estado === 'erro' && (
        <p className="flex items-center gap-2 text-xs text-dim" data-resultado-nao-carregou="">
          <span className="ponto text-erro" aria-hidden="true" />
          Os números do mês não carregaram — o motivo está no aviso acima.
        </p>
      )}
    </section>
  );
}

export default function Painel() {
  const cliente = useQueryClient();
  const [mes, setMes] = useState(mesISO());
  const competencia = `${mes}-01`;

  const equipe = useQuery({ queryKey: ['perfis'], queryFn: perfis });
  const termos = useQuery({ queryKey: ['termos'], queryFn: termosDaEquipe });
  const prevista = useFolhaPrevista();
  const folha = useQuery({
    queryKey: ['folha', competencia], queryFn: () => folhaDoMes(competencia), enabled: prevista.pronto,
  });
  const vendasMes = useQuery({
    queryKey: ['vendas-vendedor', competencia],
    queryFn: () => vendasPorVendedor(competencia),
  });
  const custos = useQuery({ queryKey: ['custos', competencia], queryFn: () => custosMensais(competencia) });
  const resultado = useQuery({ queryKey: ['resultado'], queryFn: resultadoMensal });

  const doMes = resultado.data?.find((r) => String(r.competencia).startsWith(mes));

  const porPerfil = new Map((termos.data ?? []).map((t) => [t.profile_id, t]));
  // SOMA, não sobrescreve. v_vendas_por_vendedor agrupa por (mês, id, NOME
  // CONGELADO na venda), então a mesma pessoa vira DUAS linhas no mês em que
  // o nome dela mudar no cadastro. Com `new Map(pares)` a segunda linha
  // apagava a primeira em silêncio e a comissão nascia sugerida sobre um
  // pedaço do que a pessoa vendeu. Hoje não há nome divergente no banco
  // (conferido em 11/09/2026) — o que existe é a armadilha, e ela some aqui.
  const vendidoPor = new Map<string, number>();
  for (const v of vendasMes.data ?? []) {
    const chave = v.vendedor_id ?? '';
    vendidoPor.set(chave, (vendidoPor.get(chave) ?? 0) + Number(v.total_vendido));
  }
  const lancamentoDe = new Map((folha.data ?? []).map((l) => [l.profile_id, l]));

  // Os ativos MAIS quem tem lançamento no mês (094): a folha prevista de quem
  // foi desligado continua em Contas a pagar até o dono resolver — pagar a
  // rescisão ou remover —, e é aqui que se resolve. Sem isto ela ficaria à
  // vista lá e impossível de alcançar aqui.
  const naLista = (equipe.data ?? []).filter((p) => p.ativo || lancamentoDe.has(p.id));

  // Soma do mês inteiro — nome distinto de totalFolha() (lib/folha), que
  // calcula UM lançamento. Sombrear a função importada seria pedir bug.
  const totalDoMes = (folha.data ?? []).reduce((s, l) => s + Number(l.total), 0);
  const folhaEmAberto = (folha.data ?? [])
    .filter((l) => !l.pago_em)
    .reduce((s, l) => s + Number(l.total), 0);

  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ['folha'] });
    // A mesma linha aparece em Contas a pagar e no fluxo de caixa (094).
    cliente.invalidateQueries({ queryKey: ['folha-contas'] });
    cliente.invalidateQueries({ queryKey: ['fluxo'] });
    cliente.invalidateQueries({ queryKey: ['custos'] });
    cliente.invalidateQueries({ queryKey: ['resultado'] });
    cliente.invalidateQueries({ queryKey: ['caixa'] });
  };

  const erro = equipe.error ?? termos.error ?? folha.error ?? custos.error ?? resultado.error
    ?? prevista.erro;

  const exportarFolha = () => {
    baixarCsv(`folha-${mes}.csv`, paraCsv(
      ['Funcionário', 'Papel', 'Salário', 'Comissão', 'Bônus', 'Descontos', 'Total', 'Pago em'],
      (folha.data ?? []).map((l) => [
        l.profiles?.nome ?? '', l.profiles?.papel ?? '',
        Number(l.salario_base).toFixed(2).replace('.', ','),
        Number(l.comissao).toFixed(2).replace('.', ','),
        Number(l.bonus).toFixed(2).replace('.', ','),
        Number(l.descontos).toFixed(2).replace('.', ','),
        Number(l.total).toFixed(2).replace('.', ','),
        l.pago_em ? dataBR(l.pago_em) : 'em aberto',
      ]),
    ));
  };

  return (
    <Layout titulo="Painel do dono">
      <div className="space-y-4">
        {erro ? <Aviso tipo="erro">{mensagemDeErro(erro)}</Aviso> : null}

        {/* Saúde do negócio (043): margens, EBIT, ROIC, dívida, estoque e o
            simulador de payback — as perguntas de DONO, acima do dia a dia. */}
        <SaudeNegocio />

        <section className="cartao p-4">
          <label className="rotulo" htmlFor="mes-painel">Mês de referência</label>
          <input id="mes-painel" type="month" className="campo" value={mes}
                 onChange={(e) => setMes(e.target.value)} />
        </section>

        {/* Resultado do mês */}
        <NumerosDoMes doMes={doMes}
                      estado={resultado.isPending ? 'carregando' : resultado.error ? 'erro' : 'pronto'} />

        {/* Folha de pagamento */}
        <section className="cartao p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="rotulo">Folha de pagamento</h2>
            <button type="button" onClick={exportarFolha} disabled={!folha.data?.length}
                    className="botao-terciario min-h-11 sm:min-h-8">
              Exportar CSV
            </button>
          </div>

          {folha.isLoading && <Carregando texto="Carregando folha" />}

          <ul className="mt-2 space-y-2">
            {naLista.map((p) => (
              <li key={p.id}>
                <LinhaFolha
                  profileId={p.id}
                  nome={p.nome}
                  papel={p.papel}
                  ativo={p.ativo}
                  competencia={competencia}
                  lancamento={lancamentoDe.get(p.id)}
                  salarioBase={porPerfil.get(p.id)?.salario_base ?? 0}
                  comissaoPercent={porPerfil.get(p.id)?.comissao_percent ?? 0}
                  diaPagamento={porPerfil.get(p.id)?.dia_pagamento ?? 5}
                  vendidoNoMes={vendidoPor.get(p.id) ?? 0}
                  aoMudar={recarregar}
                />
              </li>
            ))}
          </ul>

          {naLista.length === 0 && !equipe.isLoading && (
            <p className="mt-2 text-sm text-dim">
              Nenhum funcionário ativo. Cadastre a equipe primeiro.
            </p>
          )}

          {(folha.data?.length ?? 0) > 0 && (
            <div className="mt-4 flex items-baseline justify-between border-t border-fio pt-3">
              <span className="rotulo">Total do mês</span>
              <span className="text-right">
                <span className="numero block text-xl font-medium text-paper">{brl(totalDoMes)}</span>
                {folhaEmAberto > 0 && (
                  <span className="block text-xs text-mute">
                    <span className="numero text-alerta">{brl(folhaEmAberto)}</span> ainda em aberto
                  </span>
                )}
              </span>
            </div>
          )}
        </section>

        {/* Custos por categoria */}
        <section className="cartao p-4">
          <h2 className="rotulo">Para onde foi o dinheiro</h2>
          {custos.data && custos.data.length > 0 ? (
            <ul className="mt-2 divide-y divide-fio">
              {custos.data.map((c) => (
                <li key={c.categoria} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="capitalize">
                    {c.categoria.replace(/_/g, ' ')}
                    <span className="numero ml-2 text-xs text-dim">{c.lancamentos}×</span>
                  </span>
                  {/* Saída não é prejuízo: o número fica claro (Grafite). */}
                  <span className="numero text-paper">{brl(Number(c.total))}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-dim">Nenhuma saída registrada neste mês.</p>
          )}
        </section>

        {/* Histórico */}
        <section className="cartao overflow-x-auto p-4">
          <h2 className="rotulo">Últimos meses</h2>
          {resultado.data && resultado.data.length > 0 ? (
            /* O espaço entre as colunas (revisão do #359): em mono, a 390 px,
               os valores grudavam um no outro. */
            <table className="mt-2 w-full text-left text-sm [&_td]:px-2 [&_th]:px-2 [&_td:first-child]:pl-0 [&_th:first-child]:pl-0 [&_td:last-child]:pr-0 [&_th:last-child]:pr-0">
              <thead className="text-xs text-dim">
                <tr>
                  <th className="py-2">Mês</th>
                  <th className="py-2 text-right">Receitas</th>
                  <th className="py-2 text-right">Custos</th>
                  <th className="py-2 text-right">Resultado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fio">
                {resultado.data.map((r) => (
                  <tr key={String(r.competencia)}>
                    <td className="numero py-2">{String(r.competencia).slice(0, 7).split('-').reverse().join('/')}</td>
                    <td className="numero py-2 text-right text-paper">{brl(Number(r.receitas))}</td>
                    <td className="numero py-2 text-right text-mute">{brl(Number(r.custos))}</td>
                    {/* O único vermelho da tabela: resultado negativo, que é
                        prejuízo (`text-erro`), não a cor da marca. */}
                    <td className={[
                      'numero py-2 text-right',
                      Number(r.resultado) >= 0 ? 'text-paper' : 'text-erro',
                    ].join(' ')}>
                      {brl(Number(r.resultado))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="mt-2 text-sm text-dim">Sem movimento de caixa registrado ainda.</p>
          )}
        </section>
      </div>
    </Layout>
  );
}

function LinhaFolha({
  profileId, nome, papel, ativo, competencia, lancamento, salarioBase, comissaoPercent,
  diaPagamento, vendidoNoMes, aoMudar,
}: {
  profileId: string;
  nome: string;
  papel: string;
  /** Desligado com lançamento no mês também aparece (094). */
  ativo: boolean;
  competencia: string;
  /** O dia do mês em que ESTA pessoa recebe (083). */
  diaPagamento: number;
  lancamento: Awaited<ReturnType<typeof folhaDoMes>>[number] | undefined;
  salarioBase: number;
  comissaoPercent: number;
  vendidoNoMes: number;
  aoMudar: () => void;
}) {
  const comissaoSugerida = Math.round(vendidoNoMes * (comissaoPercent / 100) * 100) / 100;
  const pago = Boolean(lancamento?.pago_em);

  const [aberto, setAberto] = useState(false);
  const [salario, setSalario] = useState(String(lancamento?.salario_base ?? salarioBase ?? 0));
  // 094 (revisão): a linha PREVISTA nasce com comissão zero, e o `??` parava
  // nele — a sugestão sumia. comissaoDoCampo devolve a sugestão para a
  // prevista, como era quando o mês começava sem lançamento.
  const [comissao, setComissao] = useState(String(comissaoDoCampo(lancamento, comissaoSugerida)));
  const [bonus, setBonus] = useState(String(lancamento?.bonus ?? 0));
  const [descontos, setDescontos] = useState(String(lancamento?.descontos ?? 0));
  const [forma, setForma] = useState<PaymentMethod>('pix');

  // Folha, termos e vendas do mês chegam em requisições SEPARADAS, e a linha
  // monta antes de todas responderem. Este efeito sincroniza os campos com o
  // que veio do servidor — mas só ATÉ o dono começar a digitar.
  //
  // A versão anterior não tinha essa trava e reescrevia os quatro campos toda
  // vez que qualquer uma das consultas respondia. Na prática: você digitava o
  // salário, a consulta de vendas do mês chegava meio segundo depois, o efeito
  // disparava e zerava o campo — e "Lançar na folha" gravava o valor velho.
  // Era exatamente o "adiciono valores e não atualiza".
  const [tocado, setTocado] = useState(false);
  useEffect(() => {
    if (tocado) return;
    setSalario(String(lancamento?.salario_base ?? salarioBase ?? 0));
    setComissao(String(comissaoDoCampo(lancamento, comissaoSugerida)));
    setBonus(String(lancamento?.bonus ?? 0));
    setDescontos(String(lancamento?.descontos ?? 0));
  }, [
    tocado,
    lancamento?.salario_base, lancamento?.comissao, lancamento?.bonus,
    lancamento?.descontos, lancamento?.prevista, salarioBase, comissaoSugerida,
  ]);

  const composicao = {
    salario_base: paraNumero(salario) ?? 0,
    comissao: paraNumero(comissao) ?? 0,
    bonus: paraNumero(bonus) ?? 0,
    descontos: paraNumero(descontos) ?? 0,
  };
  const total = totalFolha(composicao);
  const invalido = validarFolha(composicao);
  // A prevista de quem tem comissão não se paga direto: o dono confere a
  // comissão acima e salva (prevista = false) — o banco recusa o mesmo
  // (pagar_folha). Sem isto, o "Pagar" levava só o salário-base.
  const semComissao = faltaAComissao(lancamento, comissaoPercent);

  const salvar = useMutation({
    mutationFn: async () => {
      if (invalido) throw new Error(invalido);
      await salvarLancamentoFolha({
        profile_id: profileId,
        competencia,
        // 083: a folha em aberto entra no fluxo de caixa NO DIA desta
        // pessoa. Sem isto, as cinco caíam todas no dia 1.
        vencimento: lancamento?.vencimento ?? dataDoPagamento(competencia, diaPagamento),
        ...composicao,
      });
    },
    // Gravou: o servidor volta a mandar no valor dos campos.
    onSuccess: () => { setTocado(false); aoMudar(); },
  });

  // O dia do pagamento: nulo = o dono não mexeu, vale a sugestão (094: o
  // vencimento se já passou, senão hoje). Derivado em vez de copiado num
  // efeito porque o lançamento chega depois da linha montar.
  const hoje = hojeISO();
  const [diaDigitado, setDiaDigitado] = useState<string | null>(null);
  const diaPago = diaDigitado ?? diaDePagarAFolha(lancamento?.vencimento, hoje);
  const diaInvalido = validarDiaPago(diaPago, hoje);

  const pagar = useMutation({
    mutationFn: () => pagarFolha(lancamento!.id, forma, diaPago),
    onSuccess: aoMudar,
  });

  const remover = useMutation({
    mutationFn: () => removerLancamentoFolha(lancamento!.id),
    onSuccess: () => { setAberto(false); aoMudar(); },
  });

  return (
    <div className="rounded-[0.625rem] border border-edge">
      <button type="button" onClick={() => setAberto(!aberto)}
              className="flex min-h-11 w-full items-center justify-between gap-3 p-3 text-left">
        <div className="min-w-0">
          <p className="truncate font-medium">{nome}</p>
          <p className="text-xs text-dim">
            {papel}
            {!ativo && ' · desligado'}
            {vendidoNoMes > 0 && ` · vendeu ${brl(vendidoNoMes)}`}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1 text-right">
          <p className="numero text-paper">
            {lancamento ? brl(Number(lancamento.total)) : <span className="text-dim">—</span>}
          </p>
          {/* A situação em SELO (Grafite): paga, em aberto; sem lançamento é
              só texto. As palavras são as de sempre. */}
          <p className={pago ? 'selo selo-pago' : lancamento ? 'selo selo-hoje' : 'text-xs text-dim'}>
            {pago ? `pago ${dataBR(lancamento!.pago_em)}`
              : lancamento ? (semComissao ? 'em aberto · falta a comissão'
                : lancamento.prevista ? 'em aberto · pelo combinado' : 'em aberto')
              : 'sem lançamento'}
          </p>
        </div>
      </button>

      {aberto && (
        <div className="space-y-3 border-t border-fio p-3">
          {pago ? (
            <Aviso tipo="ok">
              Pago em {dataBR(lancamento!.pago_em)} via {lancamento!.forma_pagamento}.
              A saída já está lançada no caixa e não pode ser editada aqui — para corrigir,
              desfaça o pagamento em <Link to="/contas" className="underline">Contas a pagar</Link>{' '}
              (aba Pagas) e lance de novo.
            </Aviso>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="rotulo" htmlFor={`s-${profileId}`}>Salário (R$)</label>
                  <input id={`s-${profileId}`} className="campo py-2" inputMode="decimal"
                         value={salario} onChange={(e) => { setTocado(true); setSalario(e.target.value); }} />
                </div>
                <div>
                  <label className="rotulo" htmlFor={`c-${profileId}`}>Comissão (R$)</label>
                  <input id={`c-${profileId}`} className="campo py-2" inputMode="decimal"
                         value={comissao} onChange={(e) => { setTocado(true); setComissao(e.target.value); }} />
                  {comissaoPercent > 0 && (
                    <button type="button"
                            className="mt-1 text-xs text-signal-soft hover:underline"
                            onClick={() => { setTocado(true); setComissao(String(comissaoSugerida)); }}>
                      usar sugerido: {brl(comissaoSugerida)} ({comissaoPercent}% de {brl(vendidoNoMes)})
                    </button>
                  )}
                </div>
                <div>
                  <label className="rotulo" htmlFor={`b-${profileId}`}>Bônus (R$)</label>
                  <input id={`b-${profileId}`} className="campo py-2" inputMode="decimal"
                         value={bonus} onChange={(e) => { setTocado(true); setBonus(e.target.value); }} />
                </div>
                <div>
                  <label className="rotulo" htmlFor={`d-${profileId}`}>Descontos (R$)</label>
                  <input id={`d-${profileId}`} className="campo py-2" inputMode="decimal"
                         value={descontos} onChange={(e) => { setTocado(true); setDescontos(e.target.value); }} />
                </div>
              </div>

              <div className="flex items-baseline justify-between border-t border-fio pt-2">
                <span className="rotulo">Total</span>
                <span className={`numero text-lg font-medium ${total < 0 ? 'text-erro' : 'text-paper'}`}>
                  {brl(total)}
                </span>
              </div>

              {total < 0 && <Aviso tipo="erro">Os descontos não podem passar do total a receber.</Aviso>}
              {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}

              <button type="button" className="botao-secundario w-full py-2"
                      disabled={salvar.isPending || Boolean(invalido)}
                      onClick={() => salvar.mutate()}>
                {salvar.isPending ? 'Salvando…' : lancamento ? 'Salvar alterações' : 'Lançar na folha'}
              </button>

              {lancamento && (
                <div className="border-t border-fio pt-3">
                  <p className="rotulo">Pagar agora</p>
                  <div className="grid grid-cols-4 gap-2">
                    {FORMAS.map((f) => (
                      <button key={f} type="button" onClick={() => setForma(f)}
                              aria-pressed={forma === f}
                              className={[
                                'min-h-11 rounded-[0.625rem] border px-2 py-2 text-xs font-medium capitalize transition-colors',
                                forma === f ? 'border-paper bg-panel-alt text-paper' : 'border-edge-forte text-mute hover:text-paper',
                              ].join(' ')}>
                        {f}
                      </button>
                    ))}
                  </div>
                  <label className="rotulo mt-3 block" htmlFor={`pg-${profileId}`}>Pago em</label>
                  <input id={`pg-${profileId}`} type="date" className="campo py-2"
                         value={diaPago} max={hoje}
                         onChange={(e) => setDiaDigitado(e.target.value)} />
                  {diaInvalido && <Aviso tipo="erro">{diaInvalido}</Aviso>}
                  {semComissao && (
                    <Aviso tipo="alerta">
                      O lançamento ainda está só com o salário ({brl(Number(lancamento.total))}):
                      a comissão do mês entra quando você salvar
                      {total !== Number(lancamento.total) ? ` — o total passa a ${brl(total)}` : ''}.
                      Confira a comissão acima, toque em &quot;Salvar alterações&quot; e depois pague.
                    </Aviso>
                  )}
                  {pagar.error ? <Aviso tipo="erro">{mensagemDeErro(pagar.error)}</Aviso> : null}
                  <button type="button" className="botao-primario mt-2 w-full py-2"
                          disabled={pagar.isPending || semComissao || Boolean(diaInvalido)}
                          onClick={() => pagar.mutate()}>
                    {pagar.isPending ? 'Lançando…' : `Pagar ${brl(Number(lancamento.total))} e lançar no caixa`}
                  </button>
                  <button type="button"
                          className="mt-2 min-h-11 w-full py-2 text-xs text-mute underline hover:text-paper"
                          disabled={remover.isPending}
                          onClick={() => remover.mutate()}>
                    remover este lançamento
                  </button>
                  {/* 094: a geração recria a linha de quem segue ativo com
                      salário — remover não é o jeito de "não pagar este mês". */}
                  {ativo && salarioBase > 0 && (
                    <p className="text-center text-xs text-dim">
                      O salário do mês volta a ser gerado enquanto a pessoa estiver ativa —
                      para não pagar este mês, zere o valor e salve.
                    </p>
                  )}
                  {remover.error ? <Aviso tipo="erro">{mensagemDeErro(remover.error)}</Aviso> : null}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
